import { parseISO, isValid } from 'date-fns'

type LessonSortable = {
  date?: string | null
  time_slot_name?: string | null
  start_time?: string | null
  effective_start_time?: string | null
  time?: string | null
}

function timeSlotMinutes(name: string | null): number | null {
  if (!name) return null
  const m = name.match(/(\d{1,2}):(\d{2})/)
  if (!m) return null
  return parseInt(m[1] || '0', 10) * 60 + parseInt(m[2] || '0', 10)
}

function compareTimeSlots(a: string | null, b: string | null): number {
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1

  const minutesA = timeSlotMinutes(a)
  const minutesB = timeSlotMinutes(b)
  if (minutesA !== null && minutesB !== null && minutesA !== minutesB) {
    return minutesA - minutesB
  }

  const numA = parseInt(a, 10)
  const numB = parseInt(b, 10)
  if (!isNaN(numA) && !isNaN(numB) && numA !== numB) {
    return numA - numB
  }

  return a.localeCompare(b)
}

function parseLessonDate(value: string | null): Date | null {
  if (!value) return null
  const isoCandidate = value.length >= 10 && value[4] === '-' ? value.slice(0, 10) : value
  const iso = parseISO(isoCandidate)
  if (isValid(iso)) return iso
  const m = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
  if (!m) return null
  const parsed = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]))
  return isValid(parsed) ? parsed : null
}

function lessonTimeName(lesson: LessonSortable): string | null {
  return lesson.effective_start_time ?? lesson.time_slot_name ?? lesson.start_time ?? lesson.time ?? null
}

export function sortLessonsByDateTime<T extends LessonSortable>(lessons: T[]): T[] {
  return [...lessons].sort((a, b) => {
    const dateA = parseLessonDate(a.date ?? null)
    const dateB = parseLessonDate(b.date ?? null)
    const timeA = lessonTimeName(a)
    const timeB = lessonTimeName(b)

    if (dateA === null && dateB === null) return compareTimeSlots(timeA, timeB)
    if (dateA === null) return 1
    if (dateB === null) return -1

    const dateComparison = dateA.getTime() - dateB.getTime()
    if (dateComparison !== 0) return dateComparison

    return compareTimeSlots(timeA, timeB)
  })
}

export function sortLessonsByTimeSlotPosition<T>(
  lessons: T[],
  timeSlotNames: string[] | undefined
): T[] {
  if (!timeSlotNames || timeSlotNames.length === 0) {
    return lessons
  }
  
  const timeSlotIndexMap = new Map<string, number>()
  timeSlotNames.forEach((name, index) => {
    timeSlotIndexMap.set(name, index)
  })
  
  return [...lessons].sort((a, b) => {
    const timeSlotNameA = (a as { time_slot_name?: string }).time_slot_name
    const timeSlotNameB = (b as { time_slot_name?: string }).time_slot_name
    
    const indexA = timeSlotNameA !== undefined && timeSlotIndexMap.has(timeSlotNameA) 
      ? timeSlotIndexMap.get(timeSlotNameA)! 
      : Number.MAX_SAFE_INTEGER
    const indexB = timeSlotNameB !== undefined && timeSlotIndexMap.has(timeSlotNameB) 
      ? timeSlotIndexMap.get(timeSlotNameB)! 
      : Number.MAX_SAFE_INTEGER
    
    return indexA - indexB
  })
}
